import {useCallback, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'

import Toast from 'components/Basic/Toast'
import {AIUsageExportResult} from 'interfaces'

export type UseUsageExportOptions = {
  /** Should be a stable (module-level) function so the callback stays memoized. */
  fetchExport: () => Promise<AIUsageExportResult>
  errorMessageKey: string
}

export type UseUsageExportResult = {
  downloadExport: () => Promise<void>
  isDownloading: boolean
}

export const useUsageExport = ({
  fetchExport,
  errorMessageKey,
}: UseUsageExportOptions): UseUsageExportResult => {
  const {t} = useTranslation(['aiUsage'])
  const [isDownloading, setIsDownloading] = useState(false)
  const isDownloadingRef = useRef(false)

  // Export toasts get a fresh id per click (no customId): the Toast wrapper
  // dismisses the visible toast first, and react-toastify drops a new toast
  // that reuses the id of one still closing, so a repeat click got no feedback.
  const downloadExport = useCallback(async () => {
    const showErrorToast = (): void => {
      Toast({
        message: t(errorMessageKey),
        type: 'error',
      })
    }

    if (isDownloadingRef.current) {
      return
    }

    isDownloadingRef.current = true
    setIsDownloading(true)

    try {
      const result = await fetchExport()

      if (result.isEmpty) {
        Toast({
          message: t('aiUsage:AI_USAGE_TOAST_EXPORT_NO_RESULTS'),
          type: 'info',
        })
        return
      }

      // A 200 without a file URL would otherwise do nothing on click.
      if (!result.fileUrl) {
        showErrorToast()
        return
      }

      window.open(result.fileUrl, '_self')
    } catch {
      showErrorToast()
    } finally {
      isDownloadingRef.current = false
      setIsDownloading(false)
    }
  }, [errorMessageKey, fetchExport, t])

  return {
    downloadExport,
    isDownloading,
  }
}
